import { useUserContext } from "app/contexts/UserContext";

import type {
  UserContext_platform$data,
  Permission,
} from "app/contexts/__generated__/UserContext_platform.graphql";

export type RoleScopeKeys =
  keyof UserContext_platform$data["platform"]["roleScopes"];

/**
 * Provides a function to check if a user has a specific permission within a given role scope.
 * System permissions act as an override for accountGroup, policyCollection, and repository scopes.
 *
 * @example
 * const hasScopePermission = useHasScopePermission();
 * const canEditAccountGroup = hasScopePermission("accountGroup", "EDIT");
 */
export function useHasScopePermission() {
  const userContext = useUserContext();

  return (scope: RoleScopeKeys, requiredPermission: Permission): boolean => {
    if (!userContext || !userContext.roleScopes) {
      return false;
    }

    // Check for system override if the current scope is not 'system'
    if (scope !== "system") {
      const systemPermissions = userContext.roleScopes.system?.userPermissions;
      if (systemPermissions?.includes(requiredPermission)) {
        return true; // System permission overrides for other scopes
      }
    }

    // Proceed with the original specific scope check
    const scopeData = userContext.roleScopes[scope];

    if (!scopeData || !scopeData.userPermissions) {
      return false;
    }

    return scopeData.userPermissions.includes(requiredPermission as Permission);
  };
}
